import { styled, Theme } from '@mui/material';
import { CoreTypography } from '@youscience/core';
import { coreThemeColors } from '@youscience/theme';

const testScoreChipBase = {
  height: 'auto',
  padding: '0.25rem 0.75rem',
  borderRadius: '0.5rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: '1.125rem',
  border: '1px solid transparent',

  span: {
    padding: 0,
  },
};

export const sxStyles = {
  admissionsContainer: {
    marginTop: '4rem',
  },
  admissionsSectionTitle: (theme: Theme) => ({
    color: theme.palette.primary.main,
  }),
  keyStatisticsTitle: (theme: Theme) => ({
    padding: '1.25rem 2rem',
    borderBottom: `1px solid ${theme.palette.grey['300']}`,
  }),
  rateGraphicWrap: {
    marginRight: '4rem',
  },
  openAdmissionTitle: {
    marginTop: '0.75rem',
    fontWeight: 700,
  },
  openAdmissionDescription: (theme: Theme) => ({
    marginTop: '0.5rem',
    color: theme.palette.grey['700'],
  }),
  generalTitle: {
    marginBottom: '1rem',
  },
  statisticItemWrap: {
    marginTop: '1rem',
    marginRight: '3rem',
  },
  statisticItemTitle: {
    marginBottom: '2px',
  },
  testScoreRequirementLabel: {
    marginBottom: '0.5rem',
  },
  testScoreRequirementDescription: (theme: Theme) => ({
    marginTop: '0.5rem',
    color: theme.palette.grey['700'],
  }),
  testScoreRequirementChip: {
    filled: (theme: Theme) => ({
      ...testScoreChipBase,
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    }),
    outlined: (theme: Theme) => ({
      ...testScoreChipBase,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.common.white,
      borderColor: theme.palette.primary.main,
    }),
    soft: (theme: Theme) => ({
      ...testScoreChipBase,
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}1F`,
    }),
    neutral: (theme: Theme) => ({
      ...testScoreChipBase,
      color: theme.palette.grey['800'],
      backgroundColor: theme.palette.grey['200'],
    }),
    neutralOutlined: (theme: Theme) => ({
      ...testScoreChipBase,
      color: theme.palette.grey['700'],
      backgroundColor: theme.palette.common.white,
      borderColor: theme.palette.grey['400'],
    }),
  },
  testRequirementWrap: {
    marginTop: '4rem',
  },
  testRequirementBlock: {
    flex: '1 1 0',
    minWidth: 0,
  },
  testScoreDescription: (theme: Theme) => ({
    marginTop: '0.5rem',
    color: theme.palette.grey['600'],
  }),
  testScoreItem: {
    '&:not(:last-of-type)': {
      marginBottom: '1.5rem',
    },
  },
  testItem: {
    position: 'relative',
    maxHeight: '20px',
    backgroundColor: coreThemeColors.primary.outlinedHoverBackground,
    marginTop: '0.75rem',
    marginBottom: '1.5rem',
  },
  leftRangeScore: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    bottom: '1.688rem',
    right: '100%',
    marginRight: '0.25rem',
  },
  leftPercentRange: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    top: '1.75rem',
    right: '100%',
    marginRight: '0.25rem',
  },
  rightRangeScore: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    bottom: '1.688rem',
    left: '100%',
    marginLeft: '0.25rem',
  },
  rightPercentRange: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    top: '1.75rem',
    left: '100%',
    marginLeft: '0.25rem',
  },
  otherRequirementsWrap: {
    marginTop: '4rem',
  },
  pieChart: {
    marginRight: '2rem',
    width: '200px',
  },
  pieChartLegend: {
    maxWidth: '536px',
    width: '100%',

    '& > h5:first-of-type': {
      marginBottom: '1rem',
    },
  },
};

export const StyledAdmissionsWrap = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  width: '100%',
  marginTop: '2.25rem',
  boxShadow: '0px 5px 25px rgba(17, 77, 76, 0.1)',
  borderRadius: '0.5rem',
}));

export const StyledAdmissionsInner = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '2.5rem',
  borderBottom: `1px solid ${theme.palette.grey['300']}`,

  [theme.breakpoints.down('lg')]: {
    padding: '2.5rem',
  },

  [theme.breakpoints.down('md')]: {
    padding: '1.75rem 2rem',
  },
}));

export const StyledGeneralWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const StyledGeneralInfo = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  marginTop: '2rem',

  [theme.breakpoints.up('sm')]: {
    marginTop: 0,
  },
}));

export const StyledOpenAdmissionBox = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  flexShrink: 0,
  width: '210px',
  minHeight: '210px',
  padding: '1.5rem',
  marginRight: '4rem',
  borderRadius: '0.5rem',
  border: `1px dashed ${theme.palette.grey['400']}`,
  backgroundColor: theme.palette.grey['50'],
}));

export const StyledGeneralDivider = styled('hr')(({ theme }) => ({
  width: '100%',
  height: '1px',
  border: 0,
  margin: '1.25rem 0',
  backgroundColor: theme.palette.grey['300'],
}));

export const StyledAdmissionWebsiteLink = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 'max-content',
  fontWeight: 600,
  marginTop: '1.25rem',
  padding: '0.5rem 0',
  color: theme.palette.primary.main,

  '& > svg:first-of-type': {
    marginRight: '0.625rem',
  },

  '&:hover': {
    backgroundColor: '#EBF6F7',
  },
}));

export const StyledReportingYear = styled('p')(({ theme }) => ({
  marginTop: '0.5rem',
  fontSize: '0.75rem',
  lineHeight: '1.125rem',
  color: theme.palette.grey['500'],
}));

export const StyledTestRequirementsInner = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '3rem',
  marginTop: '1rem',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '1.5rem',
  },
}));

export const StyledTestRequirementBlockTitles = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.875rem',
  marginBottom: '0.875rem',

  '& > div:first-of-type': {
    fontSize: '0.875rem',
    fontWeight: 700,
  },
}));

export const StyledFilledPercent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'leftOffset' && prop !== 'rangeWidth',
})<{ leftOffset: number; rangeWidth: number }>(({ leftOffset, rangeWidth, theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.primary.main,
  minHeight: '20px',
  width: `${rangeWidth}%`,
  left: `${leftOffset}%`,
}));

export const StyledScoreScale = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  color: coreThemeColors.primary.light,
}));

export const StyledLeftRange = styled('div')(({ theme }) => ({
  position: 'absolute',
  minWidth: '0.25rem',
  minHeight: '28px',
  backgroundColor: theme.palette.primary.main,
  left: 0,
  bottom: '-0.25rem',
}));

export const StyledRightRange = styled('div')(({ theme }) => ({
  position: 'absolute',
  minWidth: '0.25rem',
  minHeight: '28px',
  backgroundColor: theme.palette.primary.main,
  right: 0,
  bottom: '-0.25rem',
}));

export const StyledOtherRequirementsInner = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',

  [theme.breakpoints.down('xs')]: {
    flexDirection: 'column',
  },
}));

export const StyledDemographic = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '4rem',

  '& > div:first-of-type': {
    marginBottom: '2rem',
  },

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));

export const StyledChartLegendWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  columnGap: '2rem',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

export const StyledChartLegendGroup = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOneColumn',
})<{ isOneColumn?: boolean }>(({ isOneColumn, theme }) => ({
  width: 'auto',

  [theme.breakpoints.up('sm')]: {
    width: isOneColumn ? 'auto' : 'calc(50% - 1rem)',
  },
}));

export const StyledChartLegendItem = styled(CoreTypography, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  position: 'relative',
  paddingLeft: '1rem',
  marginBottom: '1rem',

  ':before': {
    content: "''",
    position: 'absolute',
    backgroundColor: color,
    width: '0.5rem',
    height: '0.5rem',
    borderRadius: '50%',
    top: '0.35rem',
    left: 0,
  },
}));
